import { useEffect, useState } from 'preact/hooks';
import { getBrand } from 'shared/campaign-brands';

type Props = {
  logoUrl?: string;
  disclaimerHtml?: string;
  contactPhone?: string;
  contactEmail?: string;
  termsHtml?: string;
  brand?: string;
};

function TermsModal({ html, onClose }: { html: string; onClose(): void }) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      class="campaign-terms-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Terms and Conditions"
    >
      <div class="campaign-terms-modal">
        <div class="campaign-terms-modal__header">
          <h2 style="margin: 0; font-size: 20px; font-weight: 700">Terms &amp; Conditions</h2>
          <button
            type="button"
            onClick={onClose}
            class="campaign-terms-modal__close"
            aria-label="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="campaign-terms-modal__body" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}

function FooterWave() {
  return (
    <div class="campaign-footer__wave" style="color: var(--m-bg)">
      <svg viewBox="0 0 1440 56" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M720 28H0v28h1440V28h-720S1154 0 720 0 720 28 720 28z" />
      </svg>
    </div>
  );
}

export function CampaignFooter({
  logoUrl,
  disclaimerHtml,
  contactPhone,
  contactEmail,
  termsHtml,
  brand,
}: Props) {
  const year = new Date().getFullYear();
  const [showTerms, setShowTerms] = useState(false);
  const brandDef = getBrand(brand);
  const defaultLogo = brandDef.defaultLogoUrl;

  return (
    <div class="campaign-footer">
      <FooterWave />
      <div class="campaign-footer__top">
        <div class="campaign-footer__logo">
          {(logoUrl || defaultLogo) && (
            <img src={logoUrl || defaultLogo} alt="Logo" style="max-height: 36px" />
          )}
        </div>
        {(contactPhone || contactEmail) && (
          <div class="campaign-footer__contact">
            {contactPhone && (
              <a href={`tel:${contactPhone}`}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                {contactPhone}
              </a>
            )}
            {contactPhone && contactEmail && <span class="campaign-footer__divider">|</span>}
            {contactEmail && (
              <a href={`mailto:${contactEmail}`}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                {contactEmail}
              </a>
            )}
          </div>
        )}
      </div>
      {disclaimerHtml && (
        <div
          class="campaign-footer__disclaimer"
          dangerouslySetInnerHTML={{ __html: disclaimerHtml }}
        />
      )}
      <div class="campaign-footer__bottom">
        <span class="campaign-footer__copyright">
          Copyright &copy; {year} {brandDef.copyrightName}. All Rights Reserved.
        </span>
        {brandDef.termsUrl && (
          <>
            <span class="campaign-footer__divider">|</span>
            <a
              href={brandDef.termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="campaign-footer__terms-link"
              download={false}
            >
              Terms &amp; Conditions
            </a>
          </>
        )}
        {!brandDef.termsUrl && termsHtml && (
          <>
            <span class="campaign-footer__divider">|</span>
            <button
              type="button"
              class="campaign-footer__terms-link"
              onClick={() => setShowTerms(true)}
            >
              Terms &amp; Conditions
            </button>
          </>
        )}
      </div>
      {showTerms && termsHtml && (
        <TermsModal html={termsHtml} onClose={() => setShowTerms(false)} />
      )}
    </div>
  );
}
